import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { apiGet, apiPost, getApiBase } from '../../services/api';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function startOfDay(dateStr) {
  return new Date(`${dateStr}T00:00:00`);
}

function endOfDay(dateStr) {
  return new Date(`${dateStr}T23:59:59.999`);
}

function extractAuthLinks(html) {
  if (!html) return [];
  const decoded = String(html).replace(/&amp;/g, '&');
  const matches = decoded.match(/https?:\/\/[^\s"'<>]+/gi) || [];
  const seen = new Set();
  const links = [];
  matches.forEach((raw) => {
    const url = raw.replace(/[),.;]+$/g, '');
    if (!url.includes('verify-email') && !url.includes('reset-password')) {
      return;
    }
    if (seen.has(url)) return;
    seen.add(url);
    links.push(url);
  });
  return links;
}

export default function AdminDevMailboxTab({ token, isActive, handleAuthError }) {
  const [emails, setEmails] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [clearArmed, setClearArmed] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [extractedLinks, setExtractedLinks] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState({});

  const loadEmails = useCallback(
    async (pageArg, perPageArg) => {
      if (!token) return;
      const nextPage = pageArg ?? page;
      const nextPerPage = perPageArg ?? perPage;
      setLoading(true);
      setError('');
      try {
        const res = await apiGet(
          `/api/v1/dev/mailbox?page=${nextPage}&per_page=${nextPerPage}`,
          token
        );
        if (handleAuthError(res)) return;
        if (!res.ok) {
          setError('Could not load mailbox.');
          return;
        }
        const json = await res.json();
        setEmails(Array.isArray(json.data) ? json.data : []);
        const meta = json.meta || {};
        setTotal(Number(meta.total) || 0);
        setTotalPages(Math.max(1, Number(meta.pages) || 1));
        setLoaded(true);
      } catch {
        setError('Could not load mailbox.');
      } finally {
        setLoading(false);
      }
    },
    [token, handleAuthError, page, perPage]
  );

  useEffect(() => {
    if (isActive && token) {
      loadEmails(page, perPage);
    }
    // First open + page/perPage changes. loadEmails identity is omitted so
    // Refresh can call it without this effect also looping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, token, page, perPage]);

  const filteredEmails = useMemo(() => {
    const toNeedle = filterTo.trim().toLowerCase();
    const subjectNeedle = filterSubject.trim().toLowerCase();
    return emails.filter((email) => {
      const created = email.created_at ? new Date(email.created_at) : null;
      const toOk = !toNeedle || String(email.to_email || '').toLowerCase().includes(toNeedle);
      const subjectOk =
        !subjectNeedle || String(email.subject || '').toLowerCase().includes(subjectNeedle);
      const fromOk = !filterDateFrom || (created && created >= startOfDay(filterDateFrom));
      const toDateOk = !filterDateTo || (created && created <= endOfDay(filterDateTo));
      return toOk && subjectOk && fromOk && toDateOk;
    });
  }, [emails, filterTo, filterSubject, filterDateFrom, filterDateTo]);

  const closeDetail = () => {
    setSelectedId(null);
    setSelectedEmail(null);
    setExtractedLinks(null);
  };

  const clearFilters = () => {
    setFilterTo('');
    setFilterSubject('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const handleRefresh = () => {
    if (page === 1) {
      loadEmails(1, perPage);
      return;
    }
    setPage(1);
  };

  const openEmail = async (id) => {
    if (!token) return;
    setSelectedId(id);
    setSelectedEmail(null);
    setExtractedLinks(null);
    setDetailLoading(true);
    setError('');
    try {
      const res = await apiGet(`/api/v1/dev/mailbox/${id}`, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not load email.');
        return;
      }
      const json = await res.json();
      setSelectedEmail(json.data ?? null);
    } catch {
      setError('Could not load email.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    if (!token) return;
    setClearing(true);
    setError('');
    try {
      const res = await fetch(`${getApiBase()}/api/v1/dev/mailbox/all`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not clear mailbox.');
        return;
      }
      setClearArmed(false);
      closeDetail();
      setVerifyStatus({});
      if (page === 1) {
        await loadEmails(1, perPage);
      } else {
        setPage(1);
      }
    } catch {
      setError('Could not clear mailbox.');
    } finally {
      setClearing(false);
    }
  };

  const handleExtractLinks = () => {
    const source = selectedEmail?.html_body || selectedEmail?.text_body || '';
    setExtractedLinks(extractAuthLinks(source));
  };

  const handleVerify = async (event, email) => {
    event.stopPropagation();
    if (!token) return;
    setVerifyStatus((prev) => ({ ...prev, [email.id]: 'loading' }));
    try {
      const res = await apiPost('/api/v1/dev/mailbox/verify-account', { email: email.to_email }, token);
      if (handleAuthError(res)) {
        setVerifyStatus((prev) => ({ ...prev, [email.id]: null }));
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (res.status === 404 || json.status === 'not_found') {
        setVerifyStatus((prev) => ({ ...prev, [email.id]: 'not_found' }));
        return;
      }
      if (json.status === 'already_verified') {
        setVerifyStatus((prev) => ({ ...prev, [email.id]: 'already_verified' }));
        return;
      }
      if (res.ok && json.status === 'verified') {
        setVerifyStatus((prev) => ({ ...prev, [email.id]: 'verified' }));
        return;
      }
      setError('Could not verify account.');
      setVerifyStatus((prev) => ({ ...prev, [email.id]: null }));
    } catch {
      setError('Could not verify account.');
      setVerifyStatus((prev) => ({ ...prev, [email.id]: null }));
    }
  };

  const fromAddress = selectedEmail?.headers?.from || '—';
  const htmlBody = selectedEmail?.html_body;
  const hasHtml = Boolean(htmlBody && String(htmlBody).trim());
  const displayPages = Math.max(1, totalPages);
  const isFirstPage = page <= 1;
  const isLastPage = page >= displayPages;

  return (
    <div className="rwm-admin-devmailbox" data-testid="rwm-admin-devmailbox-tab">
      {error ? (
        <Alert variant="danger" data-testid="rwm-admin-devmailbox-error">
          {error}
        </Alert>
      ) : null}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Button
          variant="info"
          onClick={handleRefresh}
          disabled={loading}
          data-testid="rwm-admin-devmailbox-refresh"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
        <Button
          variant={clearArmed ? 'warning' : 'outline-danger'}
          onClick={handleClearAll}
          disabled={clearing || loading}
          data-testid="rwm-admin-devmailbox-clear"
        >
          {clearing ? 'Clearing…' : clearArmed ? 'Click again to confirm' : 'Clear All'}
        </Button>
      </div>

      <Form className="rwm-admin-devmailbox__filters mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <Form.Group>
              <Form.Label className="small text-muted mb-1" htmlFor="rwm-admin-devmailbox-filter-to">
                To
              </Form.Label>
              <Form.Control
                id="rwm-admin-devmailbox-filter-to"
                type="text"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                placeholder="Filter by recipient"
                data-testid="rwm-admin-devmailbox-filter-to"
              />
            </Form.Group>
          </div>
          <div className="col-md-3">
            <Form.Group>
              <Form.Label
                className="small text-muted mb-1"
                htmlFor="rwm-admin-devmailbox-filter-subject"
              >
                Subject
              </Form.Label>
              <Form.Control
                id="rwm-admin-devmailbox-filter-subject"
                type="text"
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                placeholder="Filter by subject"
                data-testid="rwm-admin-devmailbox-filter-subject"
              />
            </Form.Group>
          </div>
          <div className="col-md-2">
            <Form.Group>
              <Form.Label
                className="small text-muted mb-1"
                htmlFor="rwm-admin-devmailbox-filter-date-from"
              >
                From date
              </Form.Label>
              <Form.Control
                id="rwm-admin-devmailbox-filter-date-from"
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                data-testid="rwm-admin-devmailbox-filter-date-from"
              />
            </Form.Group>
          </div>
          <div className="col-md-2">
            <Form.Group>
              <Form.Label
                className="small text-muted mb-1"
                htmlFor="rwm-admin-devmailbox-filter-date-to"
              >
                To date
              </Form.Label>
              <Form.Control
                id="rwm-admin-devmailbox-filter-date-to"
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                data-testid="rwm-admin-devmailbox-filter-date-to"
              />
            </Form.Group>
          </div>
          <div className="col-md-2">
            <Button
              variant="outline-secondary"
              className="w-100"
              onClick={clearFilters}
              data-testid="rwm-admin-devmailbox-filter-clear"
            >
              Clear filters
            </Button>
          </div>
        </div>
      </Form>

      {!loaded ? (
        <div className="text-center py-4">
          {loading || isActive ? (
            <Spinner animation="border" size="sm" data-testid="rwm-admin-devmailbox-loading" />
          ) : null}
        </div>
      ) : total === 0 ? (
        <p className="text-muted mb-0" data-testid="rwm-admin-devmailbox-empty">
          Mailbox is empty.
        </p>
      ) : (
        <>
          {filteredEmails.length === 0 ? (
            <p className="text-muted mb-0" data-testid="rwm-admin-devmailbox-empty">
              No emails match the current filters.
            </p>
          ) : (
            <Table
              hover
              responsive
              size="sm"
              className="rwm-admin__table rwm-admin-devmailbox__table"
              data-testid="rwm-admin-devmailbox-list"
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>To</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmails.map((email) => {
                  const status = verifyStatus[email.id];
                  return (
                    <tr
                      key={email.id}
                      className={`rwm-admin-devmailbox__row${
                        selectedId === email.id ? ' table-active' : ''
                      }`}
                      onClick={() => openEmail(email.id)}
                      data-testid={`rwm-admin-devmailbox-row-${email.id}`}
                    >
                      <td>{email.id}</td>
                      <td>{email.to_email}</td>
                      <td>{email.subject}</td>
                      <td>{formatDate(email.created_at)}</td>
                      <td
                        className="rwm-admin-devmailbox__verify"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {status === 'verified' ? (
                          <span className="text-success fw-semibold">Verified ✓</span>
                        ) : status === 'already_verified' ? (
                          <span className="text-muted">Already verified</span>
                        ) : status === 'not_found' ? (
                          <span className="text-danger">No account</span>
                        ) : (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={status === 'loading'}
                            onClick={(event) => handleVerify(event, email)}
                            data-testid={`rwm-admin-devmailbox-verify-${email.id}`}
                          >
                            {status === 'loading' ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              'Verify'
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          <div
            className="rwm-admin-devmailbox__pagination d-flex flex-wrap align-items-center gap-2 mt-3"
            data-testid="rwm-admin-devmailbox-pagination"
          >
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isFirstPage || loading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              data-testid="rwm-admin-devmailbox-page-prev"
            >
              Previous
            </Button>
            <span className="small text-muted" data-testid="rwm-admin-devmailbox-page-label">
              Page {page} of {displayPages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isLastPage || loading}
              onClick={() => setPage((prev) => prev + 1)}
              data-testid="rwm-admin-devmailbox-page-next"
            >
              Next
            </Button>
            <Form.Select
              size="sm"
              className="rwm-admin-devmailbox__per-page"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              data-testid="rwm-admin-devmailbox-per-page"
              aria-label="Emails per page"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
        </>
      )}

      {selectedId ? (
        <Card className="mt-4 rwm-admin-devmailbox__detail" data-testid="rwm-admin-devmailbox-detail">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Email detail</span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={closeDetail}
              data-testid="rwm-admin-devmailbox-close-detail"
            >
              Close
            </Button>
          </Card.Header>
          <Card.Body>
            {detailLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : selectedEmail ? (
              <>
                <dl className="row mb-3">
                  <dt className="col-sm-2">To</dt>
                  <dd className="col-sm-10">{selectedEmail.to_email}</dd>
                  <dt className="col-sm-2">From</dt>
                  <dd className="col-sm-10">{fromAddress}</dd>
                  <dt className="col-sm-2">Subject</dt>
                  <dd className="col-sm-10">{selectedEmail.subject}</dd>
                  <dt className="col-sm-2">Date</dt>
                  <dd className="col-sm-10">{formatDate(selectedEmail.created_at)}</dd>
                </dl>

                <div className="mb-3">
                  <Button
                    variant="info"
                    size="sm"
                    onClick={handleExtractLinks}
                    data-testid="rwm-admin-devmailbox-extract-links"
                  >
                    Extract links
                  </Button>
                </div>

                {extractedLinks ? (
                  extractedLinks.length === 0 ? (
                    <Alert variant="secondary" className="mb-3">
                      No verification or password-reset links found.
                    </Alert>
                  ) : (
                    <ul className="mb-3" data-testid="rwm-admin-devmailbox-extracted-links">
                      {extractedLinks.map((url, index) => (
                        <li key={url}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`rwm-admin-devmailbox-extracted-link-${index}`}
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )
                ) : null}

                {hasHtml ? (
                  <iframe
                    srcDoc={htmlBody}
                    sandbox="allow-popups"
                    style={{
                      width: '100%',
                      height: '500px',
                      border: '1px solid var(--rwm-border)',
                    }}
                    title="Email preview"
                  />
                ) : (
                  <pre className="rwm-admin-devmailbox__body-text">
                    {selectedEmail.text_body || '(empty body)'}
                  </pre>
                )}
              </>
            ) : (
              <p className="text-muted mb-0">Could not load this email.</p>
            )}
          </Card.Body>
        </Card>
      ) : null}
    </div>
  );
}
